import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, X, GraduationCap, IdCard, CalendarDays, BookOpen, FileText, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { api } from "../lib/api";

export default function HomeRecordSearch() {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const lookup = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setBusy(true); setResult(null);
    try {
      const { data } = await api.studentLookup(q.trim());
      setResult(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No record found for this ABC ID or phone number");
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="home-record-search" className="bg-gradient-to-br from-[#0b3b2e] via-forest to-[#123a5c] text-bone py-16 md:py-24 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #d4af37 0, transparent 40%), radial-gradient(circle at 80% 70%, #d4af37 0, transparent 40%)" }} />
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 relative z-10">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Student Portal</p>
            <h2 className="font-serif text-3xl md:text-5xl leading-tight">Check your student record instantly.</h2>
            <p className="text-bone/70 mt-5 max-w-md">Enter your ABC ID or registered phone number to view your enrolment details — department, semester, year and subjects offered.</p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="bg-bone/5 border border-bone/15 rounded-sm p-6 md:p-8 backdrop-blur-sm">
              <form onSubmit={lookup} className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-bone/50" />
                  <input data-testid="home-record-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="ABC ID or phone number" className="w-full bg-bone/10 border border-bone/20 focus:border-gold outline-none pl-9 pr-3 py-3 rounded-sm text-sm text-bone placeholder:text-bone/40" />
                </div>
                <button data-testid="home-record-btn" type="submit" disabled={busy} className="inline-flex items-center justify-center gap-2 bg-gold text-forest px-6 py-3 rounded-sm text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform disabled:opacity-60">
                  {busy ? "Searching…" : "View Record"}
                </button>
              </form>

              {result && (
                <div data-testid="home-record-result" className="mt-6 space-y-3">
                  {result.records.map((r, i) => (
                    <div key={r.id || i} className="bg-bone text-forest rounded-sm p-5">
                      <div className="flex items-start gap-4">
                        {r.photo && <img src={r.photo} alt={r.name} data-testid="home-record-photo" className="w-20 h-24 object-cover rounded-sm border border-line flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h3 className="font-serif text-xl">{r.name}</h3>
                            {i === 0 && <button data-testid="home-record-clear" onClick={() => { setResult(null); setQ(""); }} className="text-muted hover:text-forest"><X size={18} /></button>}
                          </div>
                          <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                            <p className="flex items-center gap-2"><IdCard size={15} className="text-gold" /> ABC ID: <span className="font-mono">{r.abc_id || "—"}</span></p>
                            <p className="flex items-center gap-2"><GraduationCap size={15} className="text-gold" /> {r.department || "—"} · {r.semester}</p>
                            <p className="flex items-center gap-2"><CalendarDays size={15} className="text-gold" /> Year: {r.year || "—"}</p>
                            <p className="flex items-start gap-2 sm:col-span-2"><BookOpen size={15} className="text-gold mt-0.5" /> <span>Subjects: {(r.subjects || []).length ? (r.subjects || []).join(", ") : <span className="text-muted italic">not assigned yet</span>}</span></p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* ID card status */}
                  <div data-testid="home-idcard-status" className="bg-bone/90 text-forest rounded-sm p-4 flex items-center gap-3 text-sm">
                    <IdCard size={16} className="text-gold" />
                    {result.idcard ? (
                      result.idcard.approved ? (
                        <span className="flex items-center gap-2"><CheckCircle2 size={15} className="text-green-600" /> ID Card <strong>{result.idcard.card_no}</strong> approved — <Link to="/id-card" className="underline text-gold">view / download</Link></span>
                      ) : (
                        <span className="flex items-center gap-2"><Clock size={15} className="text-amber-600" /> ID Card request pending admin approval</span>
                      )
                    ) : (
                      <span className="text-muted">No ID card request found — <Link to="/id-card" className="underline text-gold">apply here</Link></span>
                    )}
                  </div>

                  {/* Marksheets */}
                  {result.marksheets && result.marksheets.length > 0 && (
                    <div data-testid="home-marksheets" className="bg-bone/90 text-forest rounded-sm p-4">
                      <p className="text-xs uppercase tracking-widest text-gold mb-2">Marksheets / Documents</p>
                      <ul className="space-y-1.5 text-sm">
                        {result.marksheets.map((m, i) => (
                          <li key={m.id || i} className="flex items-center gap-2">
                            <FileText size={14} className="text-gold" />
                            {m.document} — {m.year}
                            <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${m.collected ? "bg-forest/10 text-forest" : m.available ? "bg-amber-500/15 text-amber-700" : "bg-muted/10 text-muted"}`}>
                              {m.collected ? "Collected" : m.available ? "Ready — come & collect" : "Processing"}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
